import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ArrowLeft, Pencil, Trash2, Plus, Mail, Phone, Globe, Eye, KeyRound } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Skeleton from '../components/ui/Skeleton';
import { EmptyServices, EmptyCredentials, ErrorState } from '../components/ui/EmptyState';
import EditSupplierModal from '../components/suppliers/EditSupplierModal';
import DeleteSupplierModal from '../components/suppliers/DeleteSupplierModal';
import AddServiceModal from '../components/services/AddServiceModal';
import EditServiceModal from '../components/services/EditServiceModal';
import DeleteServiceModal from '../components/services/DeleteServiceModal';
import CredentialWarningModal from '../components/credentials/CredentialWarningModal';
import ViewCredentialModal from '../components/credentials/ViewCredentialModal';
import AddCredentialModal from '../components/credentials/AddCredentialModal';
import EditCredentialModal from '../components/credentials/EditCredentialModal';
import DeleteCredentialModal from '../components/credentials/DeleteCredentialModal';
import { useSupplier } from '../hooks/useSuppliers';
import { useServicesBySupplier } from '../hooks/useServices';
import { useCredentials } from '../hooks/useCredentials';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';

const InfoField = ({ label, icon: Icon, children }) => (
    <div className="space-y-1">
        <span className="label-base flex items-center gap-1">
            {Icon && <Icon className="w-3 h-3" />} {label}
        </span>
        <div className="text-sm text-text-primary">{children}</div>
    </div>
);

const SupplierDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { data: supplier, isLoading: loading, error: supplierError } = useSupplier(id);
    const { data: services = [], isLoading: servicesLoading } = useServicesBySupplier(id);
    const error = supplierError?.message || '';
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    
    const [isAddServiceModalOpen, setIsAddServiceModalOpen] = useState(false);
    const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);
    const [isDeleteServiceModalOpen, setIsDeleteServiceModalOpen] = useState(false);
    const [selectedService, setSelectedService] = useState(null);

    const { data: credentials = [], isLoading: credentialsLoading } = useCredentials(id);
    const [isAddCredentialOpen, setIsAddCredentialOpen] = useState(false);
    const [isCredentialWarningOpen, setIsCredentialWarningOpen] = useState(false);
    const [isViewCredentialOpen, setIsViewCredentialOpen] = useState(false);
    const [isEditCredentialOpen, setIsEditCredentialOpen] = useState(false);
    const [isDeleteCredentialOpen, setIsDeleteCredentialOpen] = useState(false);
    const [selectedCredential, setSelectedCredential] = useState(null);

    const handleViewCredential = (credential) => {
        setSelectedCredential(credential);
        setIsCredentialWarningOpen(true);
    };

    const handleWarningConfirm = () => {
        setIsCredentialWarningOpen(false);
        setIsViewCredentialOpen(true);
    };

    const handleEditCredential = (credential) => {
        setSelectedCredential(credential);
        setIsEditCredentialOpen(true);
    };

    const handleDeleteCredential = (credential) => {
        setSelectedCredential(credential);
        setIsDeleteCredentialOpen(true);
    };

    const handleSupplierDeleted = () => {
        navigate('/suppliers');
    };

    const handleEditService = (service) => {
        setSelectedService(service);
        setIsEditServiceModalOpen(true);
    };

    const handleDeleteService = (service) => {
        setSelectedService(service);
        setIsDeleteServiceModalOpen(true);
    };

    if (loading) {
        return (
            <div className="space-y-5">
                <div className="flex items-center gap-3">
                    <Skeleton variant="button" width={80} />
                    <Skeleton variant="title" width="40%" />
                </div>
                <Card>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Skeleton variant="text" count={3} />
                        <Skeleton variant="text" count={3} />
                    </div>
                </Card>
            </div>
        );
    }

    if (error || !supplier) {
        return (
            <div className="space-y-5">
                <ErrorState message={error || t(KEYS.SUPPLIERS_NOT_FOUND)} />
                <div className="flex justify-center">
                    <Button variant="ghost" onClick={() => navigate('/suppliers')}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        {t(KEYS.SUPPLIERS_BACK)}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                    <button 
                        onClick={() => navigate('/suppliers')}
                        className="p-1.5 rounded-md text-text-muted hover:text-accent hover:bg-surface-overlay transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <h1 className="text-2xl font-semibold text-text-primary">
                        {supplier.name}
                    </h1>
                </div>
                <div className="flex gap-2">
                    <Button 
                        variant="ghost"
                        size="small"
                        onClick={() => setIsEditModalOpen(true)}
                        className="gap-1.5"
                    >
                        <Pencil className="w-3.5 h-3.5" />
                        {t(KEYS.COMMON_EDIT)}
                    </Button>
                    <Button 
                        variant="danger"
                        size="small"
                        onClick={() => setIsDeleteModalOpen(true)}
                        className="gap-1.5"
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                        {t(KEYS.COMMON_DELETE)}
                    </Button>
                </div>
            </div>

            {/* Supplier Information */}
            <Card>
                <h2 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
                    {t(KEYS.SERVICE_DETAIL_DETAILS)}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {supplier.description && (
                        <InfoField label={t(KEYS.COMMON_DESCRIPTION)} className="md:col-span-2">
                            <span className="text-text-secondary">{supplier.description}</span>
                        </InfoField>
                    )}

                    {supplier.email && (
                        <InfoField label={t(KEYS.COMMON_EMAIL)} icon={Mail}>
                            <a href={`mailto:${supplier.email}`} className="text-accent hover:underline">
                                {supplier.email}
                            </a>
                        </InfoField>
                    )}

                    {supplier.phone && (
                        <InfoField label={t(KEYS.COMMON_PHONE)} icon={Phone}>
                            <a href={`tel:${supplier.phone}`} className="text-accent hover:underline font-mono text-xs">
                                {supplier.phone}
                            </a>
                        </InfoField>
                    )}

                    {supplier.website && (
                        <InfoField label={t(KEYS.COMMON_WEBSITE)} icon={Globe}>
                            <a href={supplier.website} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                                {supplier.website}
                            </a>
                        </InfoField>
                    )}
                </div>
            </Card>

            {/* Services Section */}
            <Card padding="none">
                <div className="flex items-center justify-between px-4 py-3 border-b border-surface-border">
                    <h2 className="text-sm font-medium text-text-secondary uppercase tracking-wider">
                        {t(KEYS.SERVICES_TITLE)}
                    </h2>
                    <Button 
                        variant="primary" 
                        size="small"
                        onClick={() => setIsAddServiceModalOpen(true)}
                        className="gap-1.5"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        {t(KEYS.COMMON_ADD)}
                    </Button>
                </div>

                {servicesLoading ? (
                    <div className="p-4 space-y-3">
                        {[1, 2].map((i) => (
                            <div key={i} className="p-3 bg-surface-overlay rounded">
                                <Skeleton variant="text" count={2} />
                            </div>
                        ))}
                    </div>
                ) : services.length === 0 ? (
                    <div className="p-4">
                        <EmptyServices onAdd={() => setIsAddServiceModalOpen(true)} />
                    </div>
                ) : (
                    <div className="divide-y divide-surface-border">
                        {services.map((service, index) => (
                            <div 
                                key={service.uid} 
                                className="flex items-center justify-between px-4 py-3 hover:bg-surface-overlay transition-colors group animate-appear cursor-pointer"
                                style={{ animationDelay: `${index * 50}ms` }}
                                onClick={() => navigate(`/services/${service.uid}`)}
                            >
                                <div className="flex-1 min-w-0 mr-3">
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-sm font-medium text-text-primary truncate">
                                            {service.name}
                                        </h3>
                                        <span className="text-[10px] text-text-muted uppercase tracking-wider shrink-0">
                                            {service.type}
                                        </span>
                                    </div>
                                    {service.description && (
                                        <p className="text-xs text-text-muted mt-0.5 ml-3.5 truncate">
                                            {service.description}
                                        </p>
                                    )}
                                </div>
                                <div className="flex gap-1 shrink-0">
                                    <Button 
                                        variant="icon" 
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); handleEditService(service); }}
                                        className="!p-2.5 sm:!p-1.5"
                                    >
                                        <Pencil className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                    </Button>
                                    <Button 
                                        variant="icon" 
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); handleDeleteService(service); }}
                                        className="hover:!text-status-overdue !p-2.5 sm:!p-1.5"
                                    >
                                        <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Credentials Section */}
            <Card padding="none">
                <div className="flex items-center justify-between px-4 py-3 border-b border-surface-border">
                    <h2 className="text-sm font-medium text-text-secondary uppercase tracking-wider flex items-center gap-2">
                        <KeyRound className="w-3.5 h-3.5" />
                        {t(KEYS.CREDENTIAL_VIEW_TITLE)}
                    </h2>
                    <Button
                        variant="primary"
                        size="small"
                        onClick={() => setIsAddCredentialOpen(true)}
                        className="gap-1.5"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        {t(KEYS.COMMON_ADD)}
                    </Button>
                </div>

                {credentialsLoading ? (
                    <div className="p-4 space-y-3">
                        {[1, 2].map((i) => (
                            <div key={i} className="p-3 bg-surface-overlay rounded">
                                <Skeleton variant="text" count={2} />
                            </div>
                        ))}
                    </div>
                ) : credentials.length === 0 ? (
                    <div className="p-4">
                        <EmptyCredentials onAdd={() => setIsAddCredentialOpen(true)} />
                    </div>
                ) : (
                    <div className="divide-y divide-surface-border">
                        {credentials.map((credential, index) => (
                            <div
                                key={credential.uid}
                                className="flex items-center justify-between px-4 py-3 hover:bg-surface-overlay transition-colors group animate-appear"
                                style={{ animationDelay: `${index * 50}ms` }}
                            >
                                <div className="flex-1 min-w-0 mr-3">
                                    <h3 className="text-sm font-medium text-text-primary truncate">
                                        {credential.title || 'Untitled'}
                                    </h3>
                                    {credential.link && (
                                        <p className="text-xs text-text-muted mt-0.5 truncate">
                                            {credential.link}
                                        </p>
                                    )}
                                </div>
                                <div className="flex gap-1 shrink-0">
                                    <Button
                                        variant="icon"
                                        size="small"
                                        onClick={() => handleViewCredential(credential)}
                                        title="View credentials"
                                        className="!p-2.5 sm:!p-1.5"
                                    >
                                        <Eye className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                    </Button>
                                    <Button
                                        variant="icon"
                                        size="small"
                                        onClick={() => handleEditCredential(credential)}
                                        title="Edit credential"
                                        className="!p-2.5 sm:!p-1.5"
                                    >
                                        <Pencil className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                    </Button>
                                    <Button
                                        variant="icon"
                                        size="small"
                                        onClick={() => handleDeleteCredential(credential)}
                                        className="hover:!text-status-overdue !p-2.5 sm:!p-1.5"
                                        title="Delete credential"
                                    >
                                        <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Modals */}
            <EditSupplierModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                supplier={supplier}
            />

            <DeleteSupplierModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onSupplierDeleted={handleSupplierDeleted}
                supplier={supplier}
            />

            <AddServiceModal
                isOpen={isAddServiceModalOpen}
                onClose={() => setIsAddServiceModalOpen(false)}
                supplierUid={id}
            />

            <EditServiceModal
                isOpen={isEditServiceModalOpen}
                onClose={() => setIsEditServiceModalOpen(false)}
                service={selectedService}
                supplierUid={id}
            />

            <DeleteServiceModal
                isOpen={isDeleteServiceModalOpen}
                onClose={() => setIsDeleteServiceModalOpen(false)}
                service={selectedService}
                supplierUid={id}
            />

            <CredentialWarningModal
                isOpen={isCredentialWarningOpen}
                onClose={() => setIsCredentialWarningOpen(false)}
                onConfirm={handleWarningConfirm}
            />

            <ViewCredentialModal
                isOpen={isViewCredentialOpen}
                onClose={() => setIsViewCredentialOpen(false)}
                credentialUid={selectedCredential?.uid}
            />

            <AddCredentialModal
                isOpen={isAddCredentialOpen}
                onClose={() => setIsAddCredentialOpen(false)}
                supplierUid={id}
            />

            <EditCredentialModal
                isOpen={isEditCredentialOpen}
                onClose={() => setIsEditCredentialOpen(false)}
                credentialUid={selectedCredential?.uid}
                supplierUid={id}
            />

            <DeleteCredentialModal
                isOpen={isDeleteCredentialOpen}
                onClose={() => setIsDeleteCredentialOpen(false)}
                credential={selectedCredential}
                supplierUid={id}
            />
        </div>
    );
};

export default SupplierDetail;
